import iframemanager from '@orestbida/iframemanager';
import '@orestbida/iframemanager/dist/iframemanager.css';

// All blocked embeds are gated by the "functional" (external media) category.
export const EMBED_CATEGORY = 'functional';

type Manager = ReturnType<typeof iframemanager>;
let manager: Manager | null = null;

interface Texts {
  notice: string;
  loadBtn: string;
  loadAllBtn: string;
}

const TEXTS: Record<'de' | 'en', (provider: string) => Texts> = {
  de: (p) => ({
    notice: `Hier werden externe Inhalte von ${p} geladen. Dabei können Daten an ${p} übertragen werden.`,
    loadBtn: 'Inhalt laden',
    loadAllBtn: `${p} immer laden`,
  }),
  en: (p) => ({
    notice: `This will load external content from ${p}. Data may be transmitted to ${p}.`,
    loadBtn: 'Load content',
    loadAllBtn: `Always load ${p}`,
  }),
};

function service(lang: 'de' | 'en', provider: string, embedUrl: string, thumbnailUrl?: string) {
  const t = TEXTS[lang](provider);
  return {
    embedUrl,
    thumbnailUrl,
    iframe: {
      allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen;',
    },
    languages: {
      [lang]: { notice: t.notice, loadBtn: t.loadBtn, loadAllBtn: t.loadAllBtn },
    },
  };
}

/**
 * Wire up iframemanager (MIT, by the CookieConsent author). The WordPress side
 * rewrites raw provider iframes into iframemanager markup
 * (`<div data-service="…" data-id="…">`); this loads/blocks them per consent.
 *
 * @param hooks.acceptCategory persists "functional" consent when a user clicks a
 *        placeholder's load button (so it survives reloads and updates GTM).
 */
export function setupContentBlocker(lang: 'de' | 'en', hooks: { acceptCategory: () => void }): void {
  if (manager) return;
  manager = iframemanager();
  manager.run({
    currLang: lang,
    services: {
      youtube: service(lang, 'YouTube', 'https://www.youtube-nocookie.com/embed/{data-id}', 'https://i.ytimg.com/vi/{data-id}/hqdefault.jpg'),
      vimeo: service(lang, 'Vimeo', 'https://player.vimeo.com/video/{data-id}'),
      // Maps & Facebook embeds carry a full URL in data-id (no simple ID).
      maps: service(lang, 'Google Maps', '{data-id}'),
      facebook: service(lang, 'Facebook', '{data-id}'),
    },
    onChange: ({ eventSource }) => {
      // A click on a placeholder's load button should persist functional consent.
      if (eventSource && eventSource.type === 'click') {
        hooks.acceptCategory();
      }
    },
  });
}

/** Load or block all managed embeds based on the functional category state. */
export function syncContentBlocker(acceptedFunctional: boolean): void {
  if (!manager) return;
  if (acceptedFunctional) {
    manager.acceptService('all');
  } else {
    manager.rejectService('all');
  }
}
